import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, Alert, CircularProgress } from '@mui/material';

export interface YouTubePlayerHandle {
  getCurrentTime: () => number;
  getDuration: () => number;
  seekTo: (seconds: number) => void;
  seekToAndPlay: (seconds: number) => void;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setPlaybackRate: (rate: number) => void;
  getPlaybackRate: () => number;
}

interface Props {
  videoId: string | null | undefined;
  playbackRate?: number;
  initialTime?: number;
  onTimeUpdate?: (seconds: number) => void;
  onDurationReady?: (seconds: number) => void;
  onReady?: () => void;
  onError?: (code: number) => void;
  onPlayingChange?: (playing: boolean) => void;
  fillContainer?: boolean;
  /** Loupe clone — no controls, muted, no time callbacks. */
  muted?: boolean;
  hideControls?: boolean;
  /** Hide spinner overlay (loupe preload). */
  suppressLoadingUI?: boolean;
}

declare global {
  interface Window {
    YT?: typeof YT;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let apiLoading: Promise<void> | null = null;

function loadYouTubeApi(): Promise<void> {
  if (window.YT?.Player) return Promise.resolve();
  if (apiLoading) return apiLoading;

  apiLoading = new Promise((resolve) => {
    const existing = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existing) {
      const check = setInterval(() => {
        if (window.YT?.Player) {
          clearInterval(check);
          resolve();
        }
      }, 100);
      return;
    }

    window.onYouTubeIframeAPIReady = () => resolve();
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });

  return apiLoading;
}

const YouTubePlayer = forwardRef<YouTubePlayerHandle, Props>(function YouTubePlayer(
  {
    videoId,
    playbackRate = 1,
    initialTime = 0,
    onTimeUpdate,
    onDurationReady,
    onReady,
    onError,
    onPlayingChange,
    fillContainer,
    muted = false,
    hideControls = false,
    suppressLoadingUI = false,
  },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<YT.Player | null>(null);
  const pollRef = useRef<number | null>(null);
  const initialTimeRef = useRef(initialTime);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const onTimeUpdateRef = useRef(onTimeUpdate);
  const onDurationReadyRef = useRef(onDurationReady);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const onPlayingChangeRef = useRef(onPlayingChange);

  useEffect(() => {
    onTimeUpdateRef.current = onTimeUpdate;
  }, [onTimeUpdate]);
  useEffect(() => {
    onDurationReadyRef.current = onDurationReady;
  }, [onDurationReady]);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  useEffect(() => {
    onPlayingChangeRef.current = onPlayingChange;
  }, [onPlayingChange]);
  useEffect(() => {
    initialTimeRef.current = initialTime;
  }, [initialTime]);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const destroyPlayer = useCallback(() => {
    stopPoll();
    try {
      playerRef.current?.destroy?.();
    } catch {
      /* YT may already be torn down */
    }
    playerRef.current = null;
    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }
  }, [stopPoll]);

  const startPoll = useCallback(() => {
    stopPoll();
    pollRef.current = window.setInterval(() => {
      const t = playerRef.current?.getCurrentTime?.();
      if (typeof t === 'number') onTimeUpdateRef.current?.(t);
    }, 500);
  }, [stopPoll]);

  const notifyPlayingState = useCallback((state: number) => {
    // 1 = playing, 3 = buffering (still actively playing)
    const playing = state === 1 || state === 3;
    onPlayingChangeRef.current?.(playing);
  }, []);

  useImperativeHandle(ref, () => ({
    getCurrentTime: () => playerRef.current?.getCurrentTime?.() ?? 0,
    getDuration: () => playerRef.current?.getDuration?.() ?? 0,
    seekTo: (seconds: number) => {
      playerRef.current?.seekTo?.(seconds, true);
      onTimeUpdateRef.current?.(seconds);
    },
    seekToAndPlay: (seconds: number) => {
      playerRef.current?.seekTo?.(seconds, true);
      onTimeUpdateRef.current?.(seconds);
      playerRef.current?.playVideo?.();
    },
    play: () => playerRef.current?.playVideo?.(),
    pause: () => playerRef.current?.pauseVideo?.(),
    togglePlay: () => {
      const state = playerRef.current?.getPlayerState?.();
      if (state === 1) playerRef.current?.pauseVideo?.();
      else playerRef.current?.playVideo?.();
    },
    setPlaybackRate: (rate: number) => {
      playerRef.current?.setPlaybackRate?.(rate);
    },
    getPlaybackRate: () => playerRef.current?.getPlaybackRate?.() ?? 1,
  }));

  useEffect(() => {
    if (!videoId) {
      setLoading(false);
      setError('未設定 YouTube 影片');
      return;
    }

    let destroyed = false;
    setLoading(true);
    setError(null);

    loadYouTubeApi().then(() => {
      if (destroyed || !containerRef.current) return;

      destroyPlayer();

      playerRef.current = new window.YT!.Player(containerRef.current, {
        videoId,
        width: '100%',
        height: '100%',
        playerVars: {
          rel: 0,
          modestbranding: 1,
          ...(hideControls ? { controls: 0 } : {}),
          ...(muted ? { mute: 1 } : {}),
        },
        events: {
          onReady: () => {
            if (destroyed) return;
            setLoading(false);
            startPoll();
            const reportDuration = () => {
              const duration = playerRef.current?.getDuration?.();
              if (typeof duration === 'number' && duration > 0) {
                onDurationReadyRef.current?.(duration);
                return true;
              }
              return false;
            };
            if (!reportDuration()) {
              window.setTimeout(() => {
                if (!destroyed) reportDuration();
              }, 500);
            }
            playerRef.current?.setPlaybackRate?.(playbackRate);
            const startAt = initialTimeRef.current;
            if (startAt > 0) {
              playerRef.current?.seekTo?.(startAt, true);
              onTimeUpdateRef.current?.(startAt);
            }
            const state = playerRef.current?.getPlayerState?.();
            if (typeof state === 'number') notifyPlayingState(state);
            onReadyRef.current?.();
          },
          onStateChange: (e) => {
            notifyPlayingState(e.data);
            if (e.data === 1) startPoll();
            if (e.data === 2 || e.data === 0) {
              stopPoll();
              const t = playerRef.current?.getCurrentTime?.();
              if (typeof t === 'number') onTimeUpdateRef.current?.(t);
            }
          },
          onError: (e) => {
            const messages: Record<number, string> = {
              2: '無效的影片 ID',
              5: 'HTML5 播放器錯誤',
              100: '影片不存在或已被刪除',
              101: '影片不允許嵌入播放',
              150: '影片不允許嵌入播放',
            };
            setError(messages[e.data] || '無法載入影片');
            setLoading(false);
            onErrorRef.current?.(e.data);
          },
        },
      });
    });

    return () => {
      destroyed = true;
      destroyPlayer();
    };
  }, [videoId, destroyPlayer, startPoll, stopPoll, notifyPlayingState, hideControls, muted]);

  useEffect(() => {
    playerRef.current?.setPlaybackRate?.(playbackRate);
  }, [playbackRate]);

  if (!videoId) {
    return <Alert severity="info">請在場次設定中輸入 YouTube 連結或影片 ID</Alert>;
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', ...(fillContainer ? { height: '100%' } : { aspectRatio: '16/9' }), bgcolor: '#000', borderRadius: fillContainer ? 0 : 2, overflow: 'hidden' }}>
      {loading && !suppressLoadingUI && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
          <CircularProgress color="secondary" />
        </Box>
      )}
      {error && (
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, zIndex: 2 }}>
          <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>
        </Box>
      )}
      <Box ref={containerRef} sx={{ width: '100%', height: '100%' }} />
    </Box>
  );
});

export default YouTubePlayer;
